import { useState, useEffect } from 'react'
import { Lock, EyeOff, Eye, Monitor, Smartphone } from 'lucide-react'
import { api } from '@/lib/api'

interface Session {
  sessionId: string;
  device: string;
  location?:string;
  lastActiveAt: string;
}

export default function Security() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [sessions, setSessions] = useState<Session[]>([])
  const [currentSessionId, setCurrentSessionId] = useState('')
  const [sessionsLoading, setSessionsLoading] = useState(true) 
  
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const data = await api.users.getSessions()
        setSessions(data.sessions)
        setCurrentSessionId(data.currentSessionId)
      } catch (err) {
        console.log('Sessions татахад алдаа:', err)
      } finally {
        setSessionsLoading(false)
      }
    }
    fetchSessions()
  }, [])
  const formatTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    if (days > 0) return `${days} өдрийн өмнө`
    if (hours > 0) return `${hours} цагийн өмнө`
    if (minutes > 0) return `${minutes} минутын өмнө`
    return 'Саяхан'
  }
  const handleRevoke = async ( sessionId: string) => {
    try {
      await api.users.revokeSessions(sessionId)
      setSessions(prev => prev.filter(s => s.sessionId !== sessionId))
    } catch (err) {
      console.error('Session гаргахад алдаа:', err)
    }
  }
  const handleChangePassword = async () => {
    setError('')
    setSuccess('')

    if (!currentPassword || !newPassword) {
      setError('Бүх талбарыг бөглөнө үү')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Шинэ нууц үг таарахгүй байна')
      return
    }
    if (newPassword.length < 6) {
      setError('Нууц үг хамгийн багадаа 6 тэмдэгт байх ёстой')
      return
    }

    setLoading(true)
    try {
      await api.users.changePassword({ currentPassword, newPassword })
      setSuccess('Нууц үг амжилттай шинэчлэгдлээ')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.message || 'Нууц үг солиход алдаа гарлаа')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-2xl md:rounded-3xl border border-gray-200 p-5 sm:p-7 md:p-9">
      {/* Title */}
      <h2 className="text-base md:text-lg font-bold text-gray-800 mb-6 md:mb-9">Нууцлал ба Аюулгүй байдал</h2>

      {/* Change Password Section */}
      <div className="flex flex-col gap-4 md:gap-6 mb-6 md:mb-9">
        <p className="text-xs md:text-sm font-medium text-gray-400 uppercase">Нууц үг солих</p>

        {error && (
          <div className="p-3 bg-red-50 text-error-500 rounded-xl text-sm">{error}</div>
        )}
        {success && (
          <div className="p-3 bg-green-50 text-success-500 rounded-xl text-sm">{success}</div>
        )}

        {/* Current password */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Нууц үг</label>
            <button className="text-xs md:text-sm font-medium text-primary-500 hover:text-primary-600 cursor-pointer">
              Нууц үг мартсан?
            </button>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl md:rounded-2xl px-3.5 py-3 md:py-4 shadow-xs flex items-center gap-2">
            <Lock className="size-[18px] text-gray-400 shrink-0" />
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
              className="flex-1 outline-none text-sm md:text-base text-gray-800 placeholder:text-gray-300 bg-transparent"
            />
            <button
              onClick={() => setShowCurrent(!showCurrent)}
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              {showCurrent ? <Eye className="size-[18px]" /> : <EyeOff className="size-[18px]" />}
            </button>
          </div>
        </div>

        {/* New password */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Шинэ нууц үг</label>
          <div className="bg-white border border-gray-200 rounded-xl md:rounded-2xl px-3.5 py-3 md:py-4 shadow-xs flex items-center gap-2">
            <Lock className="size-[18px] text-gray-400 shrink-0" />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              className="flex-1 outline-none text-sm md:text-base text-gray-800 placeholder:text-gray-300 bg-transparent"
            />
          </div>
        </div>

        {/* Confirm password */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Шинэ нууц үг давтах</label>
          <div className="bg-white border border-gray-200 rounded-xl md:rounded-2xl px-3.5 py-3 md:py-4 shadow-xs flex items-center gap-2">
            <Lock className="size-[18px] text-gray-400 shrink-0" />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="flex-1 outline-none text-sm md:text-base text-gray-800 placeholder:text-gray-300 bg-transparent"
            />
          </div>
        </div>

        {/* Submit button */}
        <button
          onClick={handleChangePassword}
          disabled={loading}
          className="w-full bg-primary-500 text-white rounded-full px-[18px] py-3 md:py-4 text-sm md:text-base font-semibold shadow-xs hover:bg-primary-600 transition disabled:opacity-50 cursor-pointer"
        >
          {loading ? 'Шинэчилж байна...' : 'Нууц үг шинэчлэх'}
        </button>
      </div>

      {/* Divider */}
      <hr className="border-gray-200 mb-6 md:mb-9" />

      {/* Login History Section */}
      {sessionsLoading ? (
        <p className="text-sm text-gray-400">Уншиж байна...</p>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-gray-400">Session олдсонгүй</p>
      ) : (
        <div className="flex flex-col gap-3 md:gap-4">
          {sessions.map(session => {
            const isMobile = /mobile|iphone|android|ipad/i.test(session.device.toLowerCase())
            const isCurrent = session.sessionId === currentSessionId
            return (
              <div key={session.sessionId} className="flex items-center gap-3 md:gap-4">
                <div className="border border-gray-300 rounded-full p-2.5 md:p-3 shadow-xs shrink-0">
                  {isMobile
                    ? <Smartphone className="size-4 md:size-5 text-gray-600" />
                    : <Monitor className="size-4 md:size-5 text-gray-600" />
                  }
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <p className="text-sm md:text-base font-semibold text-gray-700 truncate">{session.device}</p>
                  <p className="text-[10px] md:text-xs text-gray-400 uppercase truncate">
                    {session.location} • {isCurrent ? 'Одоо идэвхтэй' : formatTime(session.lastActiveAt)}
                  </p>
                </div>
                {isCurrent
                  ? <div className="size-2 rounded-full bg-success-500 shrink-0" />
                  : (
                    <button
                      onClick={() => handleRevoke(session.sessionId)}
                      className="text-xs md:text-sm font-semibold text-red-700 hover:text-red-800 cursor-pointer px-2 md:px-3.5 py-2 shrink-0"
                    >
                      Гаргах
                    </button>
                  )
                }
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
